import { message, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { getEmployeeProfile } from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import { isAopsChiefState } from '../../../../store/aops'
import { userState } from '../../../../store/user'
import CertificateViewerModal from '../../myHrManage/components/CertificateViewerModal'
import ContractSignModal from '../../myHrManage/components/ContractSignModal'
import ContractTable from '../../myHrManage/ui/ContractTable'
import '../orgChart.scss'
import { employmentStatusMap, workTypeMap } from '../utils/employmentMappings'
import CertificateIssueModal from './CertificateIssueModal'
import EmploymentCertificateModal from './EmploymentCertificateModal'
import LaborContractModal from './LaborContractModal'
import PersonFormModal from './PersonFormModal'
import SalaryContractModal from './SalaryContractModal'

const PersonDetailModal = ({
  open,
  onClose,
  personId,
  employeeId,
  onSuccess,
}) => {
  const [user] = useRecoilState(userState)
  const [isChief] = useRecoilState(isAopsChiefState)
  const [loading, setLoading] = useState(false)
  const [personalInfo, setPersonalInfo] = useState(null)
  const [employmentInfo, setEmploymentInfo] = useState(null)
  const [contracts, setContracts] = useState([])
  const [selectedContract, setSelectedContract] = useState(null)
  const [isSignModalOpen, setIsSignModalOpen] = useState(false)
  const [selectedCertificate, setSelectedCertificate] = useState(null)
  const [isCertificateViewerOpen, setIsCertificateViewerOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false)
  const [isLaborContractModalOpen, setIsLaborContractModalOpen] =
    useState(false)
  const [isSalaryContractModalOpen, setIsSalaryContractModalOpen] =
    useState(false)
  const [
    isEmploymentCertificateModalOpen,
    setIsEmploymentCertificateModalOpen,
  ] = useState(false)

  // 주민등록번호에서 생년월일 추출
  const parseBirthDateFromRRN = (residentRegistrationNumber) => {
    if (!residentRegistrationNumber) return null

    // 숫자만 추출 (예: "010209-3000000" -> "0102093")
    const digits = residentRegistrationNumber.replace(/\D/g, '')
    if (digits.length < 7) return null

    const yy = digits.slice(0, 2)
    const mm = digits.slice(2, 4)
    const dd = digits.slice(4, 6)
    const genderCode = digits[6]

    let century = null
    if (genderCode === '1' || genderCode === '2') {
      // 1900년대생 (단, 00년생은 2000년대로 처리)
      century = yy === '00' ? 2000 : 1900
    } else if (genderCode === '3' || genderCode === '4') {
      // 2000년대생
      century = 2000
    } else {
      // 그 외의 경우는 현재 연도를 기준으로 계산
      // 00년생은 2000년대로 처리
      const currentYearYY = Number(dayjs().format('YY'))
      if (yy === '00') {
        century = 2000
      } else {
        century = Number(yy) > currentYearYY ? 1900 : 2000
      }
    }

    const year = century + Number(yy)
    const dateString = `${year}-${mm}-${dd}`
    const parsed = dayjs(dateString, 'YYYY-MM-DD', true)

    return parsed.isValid() ? parsed : null
  }

  const fetchPersonDetail = async () => {
    if (!personId || !open) return

    // employeeId가 없으면 조회 불가
    if (!employeeId) {
      message.warning('인사정보가 등록되지 않은 직원입니다.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const res = await getEmployeeProfile(employeeId, { userId: user?.id })
      console.log('getEmployeeProfile response::', res)
      if (res.status === 200) {
        const data = res.data

        // 주민등록번호에서 생년월일 추출
        const birthDateFromRRN = parseBirthDateFromRRN(
          data.residentRegistrationNumber,
        )

        // 개인 기본 정보 설정
        setPersonalInfo({
          name: data.name || '',
          birthDate: data.birthDate || birthDateFromRRN || null,
          residentRegistrationNumber: data.residentRegistrationNumber || '',
          contact: data.contact || data.phone || '',
          phone: data.contact || data.phone || '', // 표시용
          email: data.email || '',
          address: data.address || '',
          userId: data.userId || data.user?.id || '',
          employeeId: data.employeeId || '',
        })

        // 재직 정보 설정
        setEmploymentInfo({
          employeeStatus: data.employeeStatus || 'ACTIVE', // 원본 값
          employmentStatus:
            employmentStatusMap[data.employmentStatus] || '재직', // 표시용
          teamGroupId:
            data.teamName ||
            data.team?.nameKo ||
            data.team?.name ||
            data.teamGroupId ||
            '',
          teamName: data.teamName || data.team?.nameKo || data.team?.name || '',
          position: data.position || '',
          workType: data.workType || 'FULL_TIME', // 원본 값
          workTypeDisplay: workTypeMap[data.workType] || '정규직', // 표시용
          joinDate: data.joinDate || null,
          resignationDate: data.resignationDate || null,
        })

        // contracts 매핑 (API 응답에 contracts 배열이 있는 경우)
        if (
          data.contracts &&
          Array.isArray(data.contracts) &&
          data.contracts.length > 0
        ) {
          const mappedContracts = data.contracts.map((contract, index) => {
            // 서버 상태값(status)을 화면용 한글 상태로 변환
            let displayStatus = ''
            if (contract.status === 'PENDING') {
              displayStatus = '진행중'
            } else if (contract.status === 'COMPLETED') {
              displayStatus = '완료'
            } else {
              displayStatus =
                contract.statusDisplayName || contract.status || '알수없음'
            }

            // 날짜 포맷팅 (요청일 / 기한)
            const requestDate = contract.createdAt
              ? dayjs(contract.createdAt).format('YYYY.MM.DD')
              : '-'
            const deadline = contract.contractEndDate
              ? dayjs(contract.contractEndDate).format('YYYY.MM.DD')
              : '-'

            // contractType에 따라 계약서 이름 결정
            let contractName = contract.contractName
            if (!contractName) {
              if (contract.contractType === 'SALARY') {
                contractName = '연봉 계약서'
              } else if (contract.contractType === 'EMPLOYMENT') {
                contractName = '근로 계약서'
              } else if (
                contract.itemType === 'CERTIFICATE' ||
                contract.contractType === 'CERTIFICATE'
              ) {
                contractName = '재직증명서'
              } else {
                contractName =
                  contract.contractTypeDisplayName || '표준 근로계약서'
              }
            }

            return {
              // 원본 데이터도 모두 유지
              ...contract,
              key: contract.id || index,
              status: displayStatus,
              requestDate,
              deadline,
              contractName,
            }
          })

          setContracts(mappedContracts)
          console.log('mappedContracts::', mappedContracts)
        } else {
          setContracts([])
        }

        setLoading(false)
      } else {
        console.error('인사정보 조회 실패:', res.data)
        message.error(res.data?.message || '인사정보 조회에 실패했습니다.')
        setLoading(false)
      }
    } catch (error) {
      console.error('인사정보 조회 실패:', error)
      message.error(
        error.response?.data?.message ||
          '인사정보 조회 중 오류가 발생했습니다.',
      )
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPersonDetail()
  }, [personId, employeeId, open, user?.id])

  // 모달이 닫힐 때 데이터 초기화
  useEffect(() => {
    if (!open) {
      setPersonalInfo(null)
      setEmploymentInfo(null)
      setContracts([])
    }
  }, [open])

  if (!personId || !open) return null

  if (loading || !personalInfo || !employmentInfo) {
    return (
      <AopsCommonModalLayout
        open={open}
        onClose={onClose}
        title="개인 인사 정보"
      >
        <div className="form-container">
          <div className="form-wrapper">
            <div className="person-detail-loading-container">
              <Spin size="large" />
            </div>
          </div>
        </div>
      </AopsCommonModalLayout>
    )
  }

  const personalData = [
    { label: '이름', value: personalInfo?.name },
    {
      label: '생년월일',
      value: personalInfo?.birthDate
        ? dayjs(personalInfo.birthDate).format('YYYY년 MM월 DD일')
        : '-',
    },
    { label: '연락처', value: personalInfo?.phone },
    { label: '이메일', value: personalInfo?.email },
    { label: '집주소', value: personalInfo?.address },
  ]

  const employmentData = [
    { label: '재직 상태', value: employmentInfo?.employmentStatus },
    { label: '소속 팀', value: employmentInfo?.teamName },
    { label: '직무', value: employmentInfo?.position },
    { label: '근무형태', value: employmentInfo?.workTypeDisplay },
    {
      label: '입사일',
      value: dayjs(employmentInfo?.joinDate).format('YY년 MM월 DD일'),
    },
    {
      label: '퇴사일',
      value: employmentInfo?.resignationDate
        ? dayjs(employmentInfo?.resignationDate).format('YY년 MM월 DD일')
        : '-',
    },
  ]

  const handleViewContract = (contract) => {
    const isCertificate =
      contract?.itemType === 'CERTIFICATE' ||
      contract?.contractType === 'CERTIFICATE'
    if (isCertificate) {
      setSelectedCertificate(contract)
      setIsCertificateViewerOpen(true)
      return
    }
    if (contract.status === '진행중') {
      setSelectedContract(contract)
      setIsSignModalOpen(true)
    } else {
      setSelectedContract(contract)
      setIsSignModalOpen(true)
    }
  }

  const handleCloseSignModal = () => {
    setIsSignModalOpen(false)
    setSelectedContract(null)
  }

  const handleIssueDocument = () => {
    // onClose()
    // 모달 닫기 애니메이션 완료 후 다음 모달 열기
    setIsCertificateModalOpen(true)
  }

  const handleCloseCertificateModal = () => {
    setIsCertificateModalOpen(false)
  }

  const handleRequestSignature = (documentType) => {
    if (documentType === '재직증명서') {
      // 재직증명서 모달 열기
      setIsCertificateModalOpen(false)
      setIsEmploymentCertificateModalOpen(true)
    } else if (documentType === '연봉계약서') {
      // 연봉계약서 모달 열기
      setIsCertificateModalOpen(false)
      setIsSalaryContractModalOpen(true)
    } else if (documentType === '근로계약서') {
      // 표준 근로 계약서 모달 열기
      setIsCertificateModalOpen(false)
      setIsLaborContractModalOpen(true)
    }
  }

  const handleCloseEmploymentCertificateModal = () => {
    setIsEmploymentCertificateModalOpen(false)
  }

  const handleEmploymentCertificateRequest = (purpose) => {
    // TODO: 실제 API 호출
    // await requestCertificateApi(personId, '재직증명서', purpose)
    console.log('Request employment certificate:', purpose)
  }

  const handleCloseLaborContractModal = () => {
    setIsLaborContractModalOpen(false)
  }

  const handleCloseSalaryContractModal = () => {
    setIsSalaryContractModalOpen(false)
  }

  const handleLaborContractRequest = () => {
    // 계약 요청 성공 후 계약 내역 업데이트
    fetchPersonDetail()
  }

  const handleSalaryContractRequest = () => {
    // 계약 요청 성공 후 계약 내역 업데이트
    fetchPersonDetail()
  }

  const handleEditInfo = () => {
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
  }

  const handleEditSuccess = () => {
    // 수정 성공 후 상세 정보 다시 조회
    fetchPersonDetail()
    // 부모 컴포넌트에 성공 알림 (조직도 새로고침용)
    onSuccess?.()
  }

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title={
        <>
          개인 인사 정보
          <span
            style={{
              width: '1px',
              height: '16px',
              backgroundColor: '#C7CFD7',
              margin: '0 8px',
              display: 'inline-block',
            }}
          />
          <span className="person-detail-title-name">
            {personalInfo.name} · {employmentInfo.teamName}
          </span>
        </>
      }
    >
      <div className="form-container">
        <div className="form-wrapper">
          {/* 개인 기본 정보 */}
          <div className="person-edit-section">
            <div className="person-edit-section-header">
              <h3 className="person-edit-section-title">개인 기본 정보</h3>
              <button
                className="person-edit-issue-btn"
                onClick={handleIssueDocument}
                disabled={!employeeId}
              >
                전자문서 발급
              </button>
            </div>
            <div className="person-edit-grid">
              {personalData.map((item, index) => (
                <div
                  key={index}
                  className={`form-field-row ${
                    item.label === '집주소' ? 'form-field-row-full-width' : ''
                  }`}
                >
                  <label className="form-label">{item.label}</label>
                  <div className="form-input-wrapper">
                    <div className="form-value-text">{item.value || '-'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 재직 정보 */}
          <div className="person-edit-section">
            <h3 className="person-edit-section-title">재직 정보</h3>
            <div className="person-edit-grid">
              {employmentData.map((item, index) => (
                <div key={index} className="form-field-row">
                  <label className="form-label">{item.label}</label>
                  <div className="form-input-wrapper">
                    <div className="form-value-text">{item.value || '-'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 전자 계약 내역 */}
          <div className="person-edit-section">
            <div className="person-edit-contract-header">
              <h3 className="person-edit-section-title">전자 계약 내역</h3>
            </div>
            <ContractTable
              data={contracts}
              onView={handleViewContract}
              isChief={true}
            />
          </div>

          {/* 하단 버튼 */}
          <div className="popup-footer">
            <button className="btn-cancel" onClick={onClose}>
              취소
            </button>
            <button className="btn-submit" onClick={handleEditInfo}>
              인사정보 수정
            </button>
          </div>
        </div>
      </div>

      <PersonFormModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        personalInfo={personalInfo}
        employmentInfo={employmentInfo}
        isEditMode={true}
        employeeId={employeeId}
        userId={user?.id}
        onSuccess={handleEditSuccess}
      />

      <CertificateIssueModal
        open={isCertificateModalOpen}
        onClose={handleCloseCertificateModal}
        personalInfo={personalInfo}
        employmentInfo={employmentInfo}
        onRequest={handleRequestSignature}
      />

      <LaborContractModal
        open={isLaborContractModalOpen}
        onClose={handleCloseLaborContractModal}
        personalInfo={personalInfo}
        employmentInfo={employmentInfo}
        onRequest={handleLaborContractRequest}
      />

      <SalaryContractModal
        open={isSalaryContractModalOpen}
        onClose={handleCloseSalaryContractModal}
        personalInfo={personalInfo}
        employmentInfo={employmentInfo}
        onRequest={handleSalaryContractRequest}
      />

      {/* 재직증명서 */}
      <EmploymentCertificateModal
        open={isEmploymentCertificateModalOpen}
        onClose={handleCloseEmploymentCertificateModal}
        personalInfo={personalInfo}
        employmentInfo={employmentInfo}
        employeeId={employeeId}
        userId={user?.id}
        onRequest={handleEmploymentCertificateRequest}
      />

      <ContractSignModal
        open={isSignModalOpen}
        onClose={handleCloseSignModal}
        contract={selectedContract}
        userId={personalInfo?.userId || user?.id}
        onSignComplete={fetchPersonDetail}
        mode={selectedContract?.status === '완료' ? 'view' : 'sign'}
        isChief={isChief}
      />

      <CertificateViewerModal
        open={isCertificateViewerOpen}
        onClose={() => {
          setIsCertificateViewerOpen(false)
          setSelectedCertificate(null)
        }}
        contract={selectedCertificate}
        personalInfo={personalInfo}
      />
    </AopsCommonModalLayout>
  )
}

export default PersonDetailModal
